import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Globe, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import { useI18n, LANGS } from "../context/I18nContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV = [
  { to: "/", key: "home" },
  { to: "/voitures", key: "cars" },
  { to: "/yachts", key: "yachts" },
  { to: "/villas", key: "villas" },
  { to: "/reserver", key: "reserve" },
  { to: "/contact", key: "contact" },
];

const LangSwitcher = ({ className = "" }) => {
  const { lang, setLang } = useI18n();
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="lang-switcher-btn"
          className={`flex items-center gap-1.5 text-[13px] tracking-widest uppercase text-white/70 hover:text-white transition-colors ${className}`}
        >
          <Globe className="w-4 h-4" />
          <span>{current.flag} {current.code.toUpperCase()}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0B0B0F]/95 backdrop-blur-2xl border border-white/10 text-white/85 min-w-[160px]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onSelect={() => setLang(l.code)}
            className="cursor-pointer gap-2 focus:bg-white/10 focus:text-white"
          >
            <span>{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {lang === l.code && <Check className="w-4 h-4 text-[#D4AF37]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { t, lang, setLang } = useI18n();

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0F]/80 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-home-link" className="group flex items-center">
          <Logo light />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={`text-[13px] tracking-widest uppercase transition-colors duration-300 ${
                isActive(n.to) ? "text-[#E5C378]" : "text-white/70 hover:text-white"
              }`}
            >
              {t(`nav.${n.key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden lg:block">
            <LangSwitcher />
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden text-white p-2"
                aria-label="Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0B0B0F]/95 backdrop-blur-2xl border-l border-white/10 w-[80%] sm:w-[360px]"
            >
              <div className="mt-10 flex flex-col gap-2">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.to}>
                    <Link
                      to={n.to}
                      data-testid={`mobile-nav-${n.key}`}
                      className={`font-display text-2xl py-3 border-b border-white/10 ${
                        isActive(n.to) ? "text-[#E5C378]" : "text-white/85"
                      }`}
                    >
                      {t(`nav.${n.key}`)}
                    </Link>
                  </SheetClose>
                ))}
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`mobile-lang-${l.code}`}
                      onClick={() => setLang(l.code)}
                      className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${
                        lang === l.code ? "border-[#D4AF37] text-[#E5C378]" : "border-white/15 text-white/70"
                      }`}
                    >
                      <span>{l.flag}</span> {l.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
